import {
  Component,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { QuickCreateModal } from "../board";
import { Kbd } from "../ui";
import { api } from "../../api/client";
import { useToast } from "../../hooks";
import {
  quickCreateOpen as open,
  setQuickCreateOpen as setOpen,
} from "../../stores/quickCreateStore";
import { getDisplayId } from "../../utils/displayId";
import type {
  CreateWorkItemRequest,
  ProjectId,
  WorkItem,
  WorkItemStatus,
  WorkItemType,
} from "../../api/types";

export interface GlobalQuickCreateProps {
  projectId: ProjectId | undefined;
  projectPrefix?: string;
}

const PlusIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-4 h-4"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

interface ProjectMeta {
  itemTypes: WorkItemType[];
  defaultStatus: WorkItemStatus | null;
}

const fetchProjectMeta = async (projectId: ProjectId): Promise<ProjectMeta> => {
  const [itemTypes, statuses] = await Promise.all([
    api.get<WorkItemType[]>(`/projects/${projectId}/work-items/types`),
    api.get<WorkItemStatus[]>(`/projects/${projectId}/statuses`),
  ]);
  // Resolution order matches GuidedCreateStep:43-46
  const defaultStatus =
    statuses.find((s) => s.is_default) ??
    statuses.find((s) => s.category === "todo") ??
    statuses[0] ??
    null;
  return { itemTypes, defaultStatus };
};

export const GlobalQuickCreate: Component<GlobalQuickCreateProps> = (props) => {
  const [submitting, setSubmitting] = createSignal(false);
  const { showSuccess, showError } = useToast();

  const projectIdAccessor = () => props.projectId;
  const [meta] = createResource(projectIdAccessor, fetchProjectMeta);

  // `meta.error` is read rather than calling `meta()` to detect failure —
  // the resource accessor rethrows the rejection when read while errored
  // (Solid's createResource `read()`). AppShell mounts this widget in header
  // chrome, outside the per-route `RouteErrorBoundary`, so that throw would
  // reach the app-root boundary and replace the whole viewport. Treating a
  // failed load as "not loaded yet" keeps Create disabled and the `c`
  // shortcut inert until the next refetch (nav or project switch).
  const readyMeta = () => (meta.error ? undefined : meta());

  const itemTypes = createMemo(() => readyMeta()?.itemTypes ?? []);
  const defaultStatusId = createMemo(
    () => readyMeta()?.defaultStatus?.id ?? "",
  );
  const canOpen = createMemo(
    () => Boolean(props.projectId) && Boolean(defaultStatusId()),
  );

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return; // shell chord (e.g. `g c`) already handled this
      if (e.key !== "c") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
          return;
        }
      }
      if (open()) return; // already open — let the modal own the keystroke
      if (!canOpen()) return; // no project / statuses not loaded
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));
  });

  const handleCreate = async (data: CreateWorkItemRequest) => {
    const pid = props.projectId;
    if (!pid) return;
    setSubmitting(true);
    try {
      const created = await api.post<WorkItem>(
        `/projects/${pid}/work-items`,
        data,
      );
      const displayId = getDisplayId(
        props.projectPrefix ?? "ITEM",
        created.id,
        created.item_number,
      );
      showSuccess(`Created ${displayId}: ${created.title}`);
      setOpen(false);
    } catch (e) {
      showError(
        `Failed to create item: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Show when={props.projectId}>
      {(pid) => (
        <>
          <button
            type="button"
            onClick={() => canOpen() && setOpen(true)}
            disabled={!canOpen()}
            class="inline-flex items-center gap-1.5 h-7 px-3 text-sm font-medium rounded bg-ink text-paper hover:bg-ink-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-ring"
            aria-label="Create item"
            aria-keyshortcuts="c"
          >
            <PlusIcon />
            <span class="hidden sm:inline">Create</span>
            <Kbd variant="inverse" class="hidden md:inline-flex">
              C
            </Kbd>
          </button>
          <QuickCreateModal
            // `canOpen()` as well as `open()`: the open signal is module-level
            // (FLOW-801's palette action sets it directly), so readiness has
            // to be enforced here rather than at each call site. A request
            // that arrives early is held, not dropped — the modal appears as
            // soon as the statuses land.
            open={open() && canOpen()}
            onClose={() => setOpen(false)}
            projectId={pid()}
            statusId={defaultStatusId()}
            itemTypes={itemTypes()}
            loading={submitting()}
            onCreate={handleCreate}
          />
        </>
      )}
    </Show>
  );
};

export default GlobalQuickCreate;
